import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { Pill } from "@/components/Interface/Pill/Pill";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model/changeTwoFactorAuthenticationMethod";

export type AuthenticatorSetupMethodsForm = {
  type: keyof typeof ChangeTwoFactorAuthenticationMethod | null;
};

/**
 * Small security-level chip. The pill sits on the neutral `surface-quaternary`
 * background; only the label colour changes: `info` for the secure options,
 * `warning` for the not-recommended one.
 */
const SecurityChip = ({
  text,
  variant,
}: {
  text: string;
  variant: "info" | "warning";
}) => (
  <Pill
    className={clsx(
      "bg-surface-quaternary",
      variant === "info" ? "text-foreground-info" : "text-foreground-warning",
    )}
    size="sm"
    text={text}
  />
);

const OptionLabel = ({ title, chip }: { title: string; chip: ReactNode }) => (
  <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
    <span>{title}</span>
    {chip}
  </span>
);

/** Title and security chip per 2FA method. */
const twoFactorOptionConfig = {
  APP: {
    titleKey: "2fa.setup.options.authenticatorApp.title",
    chipKey: "2fa.setup.options.very-secure",
    chipVariant: "info",
  },
  SMS: {
    titleKey: "2fa.setup.options.sms.title",
    chipKey: "2fa.setup.options.secure",
    chipVariant: "info",
  },
  STANDARD: {
    titleKey: "2fa.setup.options.login.title",
    chipKey: "2fa.setup.options.not-recommended",
    chipVariant: "warning",
  },
} as const satisfies Record<ChangeTwoFactorAuthenticationMethod, unknown>;

/** Display order of the options, most secure first. */
export const twoFactorMethodOrder = [
  ChangeTwoFactorAuthenticationMethod.APP,
  ChangeTwoFactorAuthenticationMethod.SMS,
  ChangeTwoFactorAuthenticationMethod.STANDARD,
];

export const AuthenticatorSetupFormElements = () => (
  <>
    {twoFactorMethodOrder.map((method) => (
      <TwoFactorMethodOption key={method} method={method} />
    ))}
  </>
);

/** Must render inside an ancestor `RadioGroup` (see the form components that own `useForm`). */
export const TwoFactorMethodOption = ({
  method,
}: {
  method: ChangeTwoFactorAuthenticationMethod;
}) => {
  const { t } = useTranslation("translation");
  const { titleKey, chipKey, chipVariant } = twoFactorOptionConfig[method];

  return (
    <RadioButtonOption<AuthenticatorSetupMethodsForm>
      name="type"
      value={method}
      label={
        <OptionLabel
          title={t(titleKey)}
          chip={<SecurityChip text={t(chipKey)} variant={chipVariant} />}
        />
      }
    />
  );
};
